import axios from 'axios'
import { message } from 'ant-design-vue'
import router from '../router'

// 根据环境确定API基础URL
const isProduction = process.env.NODE_ENV === 'production'
// 生产环境使用实际域名和server路径，开发环境依赖代理
const API_BASE_URL = isProduction ? '/server' : ''
const API_PREFIX = '/api'
const REQUEST_TIMEOUT = parseInt(process.env.VUE_APP_REQUEST_TIMEOUT) || 15000
const IS_DEBUG = process.env.VUE_APP_DEBUG === 'true'

// 创建axios实例
const service = axios.create({
  baseURL: isProduction ? API_BASE_URL + API_PREFIX : API_PREFIX, // 根据环境使用不同的baseURL
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 调试模式下打印配置信息
if (IS_DEBUG) {
  console.log('🔧 API配置信息:')
  console.log('- 环境:', process.env.NODE_ENV)
  console.log('- 是否生产环境:', isProduction)
  console.log('- API基础地址:', API_BASE_URL)
  console.log('- API前缀:', API_PREFIX)
  console.log('- 完整API地址:', isProduction ? API_BASE_URL + API_PREFIX : API_PREFIX)
  console.log('- 请求超时时间:', REQUEST_TIMEOUT + 'ms')
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')
    
    // 如果有token则添加到请求头
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    
    // 调试模式下打印请求信息
    if (IS_DEBUG) {
      console.log('📤 发送请求:', {
        url: config.url,
        method: config.method,
        params: config.params,
        data: config.data
      })
    }
    
    return config
  },
  error => {
    console.error('❌ 请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 调试模式下打印响应信息
    if (IS_DEBUG) {
      console.log('📥 收到响应:', {
        url: response.config.url,
        status: response.status,
        data: res
      })
    }
    
    // 如果接口返回的success为false或code不是200，则判断为错误
    if (res.success === false || (res.code && res.code !== 200 && res.code !== 0)) {
      const errorMessage = res.message || '请求失败'
      
      // 非调试模式下才显示错误消息，避免重复显示
      if (!IS_DEBUG) {
        message.error(errorMessage)
      }
      
      // 401: 未登录或Token过期
      if (res.code === 401) {
        // 清除本地token
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        
        // 跳转到登录页
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }
      }
      
      return Promise.reject(new Error(errorMessage))
    }
    
    return res
  },
  error => {
    console.error('❌ 响应错误:', error)
    
    // 添加更详细的错误信息
    const requestUrl = error.config?.url || '未知URL'
    const requestMethod = error.config?.method || '未知方法'
    
    // 处理网络错误
    let errorMessage = `网络错误: ${requestMethod.toUpperCase()} ${requestUrl} 请求失败`
    
    if (error.response) {
      const status = error.response.status
      const statusText = error.response.statusText
      
      switch (status) {
        case 401:
          errorMessage = '未授权，请重新登录'
          // 清除本地token
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          // 跳转到登录页
          if (router.currentRoute.value.path !== '/login') {
            router.push('/login')
          }
          break
        case 403:
          errorMessage = '拒绝访问'
          break
        case 404:
          errorMessage = `请求的资源不存在: ${requestUrl}`
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        case 502:
          errorMessage = '网关错误'
          break
        case 503:
          errorMessage = '服务不可用'
          break
        case 504:
          errorMessage = '网关超时'
          break
        default:
          errorMessage = `请求失败: ${status} ${statusText}`
      }
      
      // 调试模式下显示详细错误信息
      if (IS_DEBUG) {
        console.error('📊 详细错误信息:', {
          status,
          statusText,
          url: requestUrl,
          method: requestMethod,
          data: error.response.data,
          headers: error.config?.headers,
          baseURL: error.config?.baseURL
        })
      }
    } else if (error.request) {
      errorMessage = '服务器无响应，请检查网络连接'
      
      if (IS_DEBUG) {
        console.error('🌐 网络请求失败:', error.request)
      }
    } else {
      errorMessage = error.message || '请求配置错误'
      
      if (IS_DEBUG) {
        console.error('⚙️ 请求配置错误:', error.message)
      }
    }
    
    // 在生产环境中添加更清晰的错误消息
    if (isProduction) {
      console.error(`[生产环境错误] ${errorMessage}`, {
        url: requestUrl,
        method: requestMethod
      })
    }
    
    message.error(errorMessage)
    return Promise.reject(error)
  }
)

// 封装GET请求
export function get(url, params) {
  return service.get(url, { params })
}

// 封装POST请求
export function post(url, data, config = {}) {
  return service.post(url, data, config)
}

// 封装PUT请求
export function put(url, data, config = {}) {
  return service.put(url, data, config)
}

// 封装DELETE请求
export function del(url, params) {
  return service.delete(url, { params })
}

// 封装PATCH请求
export function patch(url, data, config = {}) {
  return service.patch(url, data, config)
}

// 导出配置信息（供其他模块使用）
export const apiConfig = {
  baseURL: isProduction ? API_BASE_URL + API_PREFIX : API_PREFIX,
  timeout: REQUEST_TIMEOUT,
  isDebug: IS_DEBUG
}

export default service 